// project imports
// action - state management
import * as actionTypes from "../../actions/actionTypes";
import { getUnitSymbolByUnit, itemType, unitType } from "../types";
import { years } from "store/utils/years";
import { GENERAL_ADMINISTRATIVE_EXPENSES_REDUCER_KEY } from "..";
import {
  gae_accountingCPAMonth,
  gae_bookkeeperKeyMonth,
  gae_consultantsMonth,
  gae_educationExpensesMonth,
  gae_emailSubscriptionsMonth,
  gae_fixedAssetMonth,
  gae_legalKeyMonth,
  gae_otherOperatingExpensesMonth,
  gae_rentGeneralExpensesMonth,
  gae_researchAnalysisMonth,
} from "./staticYearsData";

// This is to filter based on company types
import { SaaSCoType, GeneralCoType } from "constants/companyTypes";

const prefix = "gae_";

// other revenue keys
export const OTHER_OPERATING_EXPENSES_KEY = prefix + "otherOperatingExpenses";
export const LEGAL_KEY = prefix + "legalKey";
export const ACCOUNTING_KEY = prefix + "accountingCPA";
export const BOOKKEEPER_KEY = prefix + "bookkeeperKey";
export const RESEARCH_ANALYSIS_KEY = prefix + "researchAnalysis";
export const CONSULTANTS_KEY = prefix + "consultants";
export const RENT_AND_GENERAL_EXPENSES_KEY = prefix + "rentGeneralExpenses";
export const FIXED_ASSET_KEY = prefix + "fixedAsset";
export const EDUCATION_EXPENSES_KEY = prefix + "educationExpenses";
export const EMAIL_SUBSCRIPTIONS_KEY = prefix + "emailSubscriptions";

export const generalAdministrativeExpenseItems = [
  OTHER_OPERATING_EXPENSES_KEY,
  LEGAL_KEY,
  ACCOUNTING_KEY,
  BOOKKEEPER_KEY,
  RESEARCH_ANALYSIS_KEY,
  CONSULTANTS_KEY,
  RENT_AND_GENERAL_EXPENSES_KEY,
  FIXED_ASSET_KEY,
  EDUCATION_EXPENSES_KEY,
  EMAIL_SUBSCRIPTIONS_KEY,
];

export const initialState = {
  [OTHER_OPERATING_EXPENSES_KEY]: {
    title: "Other Operating Expenses",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.CALCULATION,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      let result = 0;
      for (let i = 1; i < generalAdministrativeExpenseItems.length; i++) {
        result += Number(
          getReducerFunc(GENERAL_ADMINISTRATIVE_EXPENSES_REDUCER_KEY)[
            generalAdministrativeExpenseItems[i]
          ]["years"][yearKey]
        );
      }
      return result;
    },
    months: gae_otherOperatingExpensesMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 0,
      [years.YEAR_3_KEY]: 0,
      [years.YEAR_4_KEY]: 0,
      [years.YEAR_5_KEY]: 0,
    },
  },
  [LEGAL_KEY]: {
    title: "Legal",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: gae_legalKeyMonth,
    years: {
      [years.YEAR_1_KEY]: 1250,
      [years.YEAR_2_KEY]: 1250,
      [years.YEAR_3_KEY]: 1250,
      [years.YEAR_4_KEY]: 1250,
      [years.YEAR_5_KEY]: 1250,
    },
  },
  [ACCOUNTING_KEY]: {
    title: "Accounting (CPA)",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: gae_accountingCPAMonth,
    years: {
      [years.YEAR_1_KEY]: 1000,
      [years.YEAR_2_KEY]: 1000,
      [years.YEAR_3_KEY]: 1000,
      [years.YEAR_4_KEY]: 1000,
      [years.YEAR_5_KEY]: 1000,
    },
  },
  [BOOKKEEPER_KEY]: {
    title: "Bookkeeper",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: gae_bookkeeperKeyMonth,
    years: {
      [years.YEAR_1_KEY]: 750,
      [years.YEAR_2_KEY]: 750,
      [years.YEAR_3_KEY]: 750,
      [years.YEAR_4_KEY]: 750,
      [years.YEAR_5_KEY]: 750,
    },
  },
  [RESEARCH_ANALYSIS_KEY]: {
    title: "Research & Analysis",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },

    months: gae_researchAnalysisMonth,
    years: {
      [years.YEAR_1_KEY]: 0,
      [years.YEAR_2_KEY]: 250,
      [years.YEAR_3_KEY]: 500,
      [years.YEAR_4_KEY]: 750,
      [years.YEAR_5_KEY]: 1000,
    },
  },
  [CONSULTANTS_KEY]: {
    title: "Consultants",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: gae_consultantsMonth,
    years: {
      [years.YEAR_1_KEY]: 500,
      [years.YEAR_2_KEY]: 750,
      [years.YEAR_3_KEY]: 1000,
      [years.YEAR_4_KEY]: 1000,
      [years.YEAR_5_KEY]: 1000,
    },
  },
  [RENT_AND_GENERAL_EXPENSES_KEY]: {
    title: "Rent and General Expenses",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: gae_rentGeneralExpensesMonth,
    years: {
      [years.YEAR_1_KEY]: 2000,
      [years.YEAR_2_KEY]: 2200,
      [years.YEAR_3_KEY]: 2400,
      [years.YEAR_4_KEY]: 2500,
      [years.YEAR_5_KEY]: 2700,
    },
  },
  [FIXED_ASSET_KEY]: {
    title: "Fixed Asset (as Expense)",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: gae_fixedAssetMonth,
    years: {
      [years.YEAR_1_KEY]: 400,
      [years.YEAR_2_KEY]: 400,
      [years.YEAR_3_KEY]: 400,
      [years.YEAR_4_KEY]: 400,
      [years.YEAR_5_KEY]: 400,
    },
  },
  [EDUCATION_EXPENSES_KEY]: {
    title: "Education Expenses",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: gae_educationExpensesMonth,
    years: {
      [years.YEAR_1_KEY]: 350,
      [years.YEAR_2_KEY]: 350,
      [years.YEAR_3_KEY]: 350,
      [years.YEAR_4_KEY]: 350,
      [years.YEAR_5_KEY]: 350,
    },
  },
  [EMAIL_SUBSCRIPTIONS_KEY]: {
    title: "Email Subscriptions",
    unit: unitType.MONEY,
    unitSymbol: getUnitSymbolByUnit(unitType.MONEY),
    initialPeriod: 0,
    initialPeriodMonth: 0,
    type: itemType.INPUT,
    companyTypes: [SaaSCoType, GeneralCoType],
    calculate: (
      yearKey,
      getReducerFunc,
      companyType = null,
      instanceId = null
    ) => {
      return 0;
    },
    months: gae_emailSubscriptionsMonth,
    years: {
      [years.YEAR_1_KEY]: 250,
      [years.YEAR_2_KEY]: 250,
      [years.YEAR_3_KEY]: 250,
      [years.YEAR_4_KEY]: 250,
      [years.YEAR_5_KEY]: 250,
    },
  },
};

// ==============================|| SUBSCRIPTION ACQUISITION ASSUMPTIONS REDUCER ||============================== //

const generalAdministrativeExpensesReducer = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.SET_GENERAL_ADMINISTRATIVE_EXPENSE_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          years: {
            ...state[action.key].years,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.SET_GENERAL_ADMINISTRATIVE_EXPENSE_BY_MONTHS:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: {
            ...state[action.key].months,
            [action.yearKey]: [action.amount],
          },
        },
      };
    case actionTypes.REPLACE_GENERAL_ADMINISTRATIVE_EXPENSE_BY_YEAR:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          months: action?.payload,
        },
      };
    case actionTypes.RESET_GENERAL_ADMINISTRATIVE_EXPENSE_REDUCER:
      return initialState;
    default:
      return state;
  }
};

export default generalAdministrativeExpensesReducer;
